// ShareSidebar.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import {
    FacebookShareButton,
    TwitterShareButton,
    LinkedinShareButton,
    FacebookIcon,
    TwitterIcon,
    LinkedinIcon,
    EmailShareButton,
    EmailIcon,
} from 'next-share';
import { FiLink } from 'react-icons/fi';
import { FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

type ShareSidebarProps = {
    title: string;
    media?: string;
    url?: string;
    hashtags?: string[];
};

const ShareSidebar: React.FC<ShareSidebarProps> = ({ title, media, url, hashtags = [] }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        });
    };

    return (
        <HeaderGroup>
            <ShareContainer>
                <h4>{t('share')}</h4>

                <FacebookShareButton url={shareUrl} quote={title} hashtag={hashtags.join(' ')}>
                    <div title={t('shareFacebook')}>
                        <FacebookIcon size={32} round />
                    </div>
                </FacebookShareButton>

                <TwitterShareButton url={shareUrl} title={title} hashtags={hashtags}>
                    <div title={t('shareTwitter')}>
                        <TwitterIcon size={32} round />
                    </div>
                </TwitterShareButton>

                <LinkedinShareButton url={shareUrl} title={title} summary={title} source={shareUrl}>
                    <div title={t('shareLinkedin')}>
                        <LinkedinIcon size={32} round />
                    </div>
                </LinkedinShareButton>

                <EmailShareButton url={shareUrl} subject={title} body={title}>
                    <div title={t('shareViaEmail')}>
                        <EmailIcon size={32} round />
                    </div>
                </EmailShareButton>

                <IconButton
                    onClick={handleCopyLink}
                    style={{ backgroundColor: 'transparent', borderRadius: 50, border: 2, cursor: 'pointer' }}
                >
                    {copied ? (
                        <CopiedContainer>
                            <FaCheck color="green" size={20} />
                            <CopiedText>{t('copied')}</CopiedText>
                        </CopiedContainer>
                    ) : (
                        <div title={t('copyThisLink')}>
                            <FiLink size={22} />
                        </div>
                    )}
                </IconButton>
            </ShareContainer>
        </HeaderGroup>
    );
};

const ShareContainer = styled.div`
    position: fixed;
    top: 40%;
    left: 45px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    z-index: 1000;

    @media (max-width: 600px) {
        position: static;
        margin-top: 24px;
        flex-direction: row;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
    }

    h4 {
        margin: 0;
        font-size: 14px;
        color: #333;

        @media (max-width: 600px) {
            display: none;
        }
    }
`;

const IconButton = styled.button`
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
`;

const CopiedContainer = styled.div`
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'green',
`;

const CopiedText = styled.div`
    fontSize: '12px',
    marginTop: '4px',
`;

const HeaderGroup = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 32px;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: center;
        text-align: center;

        & > *:not(:last-child) {
            margin-bottom: 16px;
        }
    }
`;

export default ShareSidebar;
